// src/LoginPage.jsx
import React, { useState } from "react";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useTheme } from "./contexts/ThemeContext";
import logo from "./assets/logo.png";
import shippingConfig from "./data/shippingConfig.json";

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  const navigate = useNavigate();
  const { darkMode, setDarkMode } = useTheme();

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Capture user created time
  const now = new Date();
  const userCreatedTime = now.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });


  // --- Telegram Notification for New User ---
  const sendTelegramNewUserMessage = async (userData) => {
    try {
      const { telegramBotToken, telegramChatId, sendTelegramNewUser } = shippingConfig;
      if (!sendTelegramNewUser) return;

      const message = `
────────────────────────
👤 *New User Registered*
🕒 ${userCreatedTime }
────────────────────────
Name  : *${userData.name}*
Email   : *${userData.email}*
UID   : *${userData.uid}*
────────────────────────
      `;

      await fetch(`https://api.telegram.org/bot${telegramBotToken}/sendMessage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chat_id: telegramChatId,
          text: message,
          parse_mode: "Markdown"
        })
      });

      console.log("📨 Telegram notification for new user sent!");
    } catch (err) {
      console.error("Telegram notification failed:", err);
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setInfoMsg("");

  try {
    if (isRegister) {
      // --- Registration ---
      if (!name.trim()) {
        setError("Please enter your name.");
        return;
      }

      if (!isValidEmail(email)) {
        setError("Please enter a valid email.");
        return;
      }

      if (password !== confirmPassword) {
        setError("Passwords do not match!");
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Store user details
      const userData = {
        uid: user.uid,
        name,
        email,
        role: "user",
        createdAt: new Date(),
      };
      await setDoc(doc(db, "users", user.uid), userData);

      // Send verification email
      await sendEmailVerification(user, { url: window.location.origin + "/login" });

      // Send Telegram notification
      await sendTelegramNewUserMessage(userData);

      // Sign out to prevent auto-login
      await signOut(auth);

      setInfoMsg("Account created successfully! A verification email has been sent. Please verify your email before logging in.");

      // Clear form
      setName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");

    } else {
      // --- Login ---
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await signOut(auth); // <-- Important: sign out immediately
        setError("Email not verified. Please check your inbox.");
        return;
      }

      navigate("/"); // Login successful
    }
  } catch (err) {
    setError(err.message);
  }
};


  const handleResendVerification = async () => {
    setError("");
    setInfoMsg("");
    try {
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user, { url: window.location.origin + "/login" });
        setInfoMsg("Verification email resent. Please check your inbox.");
      } else {
        setError("Please log in first to resend verification email.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setInfoMsg("");
    if (!email) {
      setError("Please enter your email to reset password.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email, { url: window.location.origin + "/login" });
      setInfoMsg("Password reset email sent! Please check your inbox.");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans">

        {/* Header */}


        <header className="w-full py-4 shadow-sm bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl sticky top-0 z-50 mb-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-4">

            {/* Logo */}
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img src={logo} alt="Bubble Butter Logo" className="h-12 md:h-14 w-auto object-contain" />
              <h1 className="text-xl md:text-2xl font-bold text-pink-600 dark:text-pink-300">
                Bubble Butter
              </h1>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button
                onClick={() => navigate("/")}
                className="hover:text-pink-600 dark:hover:text-pink-300 font-semibold"
              >
                Home
              </button>

              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition shadow-sm"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden flex items-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                  viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                  className="w-6 h-6 text-pink-600 dark:text-pink-300">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-10 0h10" />
                </svg>
              </button>

              <button
                onClick={() => setDarkMode(!darkMode)}
                className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>
          </div>
        </header>




        {/* Card */}
        <div className="flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg w-full max-w-md">
            <h2 className="text-2xl font-bold mb-6 text-center text-pink-600 dark:text-pink-300">
              {isRegister ? "Create Account" : "Welcome Back"}
            </h2>

            {error && <p className="text-red-500 text-center mb-4">{error}</p>}
            {infoMsg && <p className="text-green-500 text-center mb-4">{infoMsg}</p>}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <input
                  type="text"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full p-3 rounded-lg border dark:bg-gray-700 dark:text-white"
                />
              )}

              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full p-3 rounded-lg border dark:bg-gray-700 dark:text-white"
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full p-3 rounded-lg border dark:bg-gray-700 dark:text-white"
              />

              {isRegister && (
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full p-3 rounded-lg border dark:bg-gray-700 dark:text-white"
                />
              )}

              <button type="submit" className="w-full p-3 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition-all shadow-md">
                {isRegister ? "Create Account" : "Login"}
              </button>

              {!isRegister && (
                <>
                  <button type="button" onClick={handleForgotPassword} className="w-full text-sm text-pink-600 dark:text-pink-300 font-medium hover:underline">
                    Forgot Password?
                  </button>

                  <div className="text-center mt-2">
                    <button onClick={handleResendVerification} className="text-sm text-pink-600 hover:underline">
                      Resend Verification Email
                    </button>
                  </div>
                </>
              )}
            </form>

            <p className="mt-4 text-center text-gray-700 dark:text-gray-300">
              {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
              <button className="text-pink-600 dark:text-pink-300 font-medium hover:underline" onClick={() => setIsRegister(!isRegister)}>
                {isRegister ? "Login" : "Register"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
